import React, { useEffect, useState, useContext, useMemo } from "react";
import CenterContainer from "../components/CenterContainer";
import TestCard from "../components/TestCard";
import Button from "../components/Button";
import { faPlus, faArrowRight, faPhone, faTrash, faLink, faPen } from "@fortawesome/free-solid-svg-icons";
import AuthContext from "../AuthContext";
import { useHistory } from "react-router-dom";
import Spinner from "../components/Spinner";
import Alert from "../components/Alert";
import { getColorForTestStatus, getIconForTestStatus, OpenCloseBadge, TestStatusBadge } from "../components/Badge";
import Title from "../components/Title";
import { useLanguage } from "../useLanguage";
import { LanguageContext } from "../LanguageContext";
import Footer from "../components/Footer";
import { Region, RegionTestsRequest } from "consumerhouse-shared";
import { OpenCloseButton } from "../components/OpenCloseButton";
import styled from "styled-components";
import { ReactComponent as UndrawEmpty } from "../data/undraw_empty.svg";
import { FriendInviteCard } from "../components/FriendInviteCard";
import { Test } from "consumerhouse-shared";
import { GroupContainer } from "../components/GroupContainer";
import { List as LoadingList } from "react-content-loader";
import { capitalize } from "../util";

const UndrawContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 500px;
    font-size: 1.5em;
    text-align: center;
`;

const Toolbar = styled.div`
    display: inline-flex;
    flex-wrap: wrap;
`;

const ToolbarButton = styled(Button)`
    margin: 0.25em;
    flex-grow: 1;
`;

const ToolbarOpenCloseButton = styled(OpenCloseButton)`
    margin: 0.25em;
`;

const RegionName = styled.span`
    text-transform: capitalize;
`;

const BannerBackground = styled.div`
    width: 100%;
    height: 400px;
    background: url("banner.jpg");
    background-color: #000f;
    background-repeat: no-repeat;
    background-size: cover;
    border-radius: 1em;
    overflow: hidden;
    /* box-shadow: 3px 7px 10px #0003; */
`;

const BannerContainer = styled.div`
    padding: 1em;
    text-align: center;
    font-size: 1.15em;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: white;
    background: linear-gradient(90deg, #0004, #000b, #0004);
    width: 100%;
    height: 100%;

    @media only screen and (max-width: 600px) {
        font-size: 0.9em;
    }
`;

export default function RegionTests() {
    const { auth, client } = useContext(AuthContext);
    const [region, setRegion] = useState<Region | null>();
    const history = useHistory();
    const { language: languageContext } = useContext(LanguageContext);
    const language = useLanguage();

    useEffect(() => {
        (async () => {
            let regionNameOverride = window.location.hash ? decodeURIComponent(window.location.hash.slice(1)) : undefined;
            if (auth) {
                try {
                    setRegion(
                        (
                            await client.fetch("/region/tests", {
                                regionName: regionNameOverride ?? auth?.user.activeRegionName ?? undefined,
                            })
                        ).region
                    );
                } catch (ex) {
                    console.error("Could not get region tests", ex);
                    history.push("/select-region");
                }
            } else {
                try {
                    setRegion((await client.fetch("/region/tests/public")).region);
                } catch (ex) {
                    console.error("Could not get region tests", ex);
                    setRegion({ name: "Antwerpen", testsInRegion: [], open: true });
                }
            }
        })();
    }, [auth]);

    return (
        <>
            <CenterContainer style={{ minHeight: "80vh", margin: "2em 0" }}>
                {auth && auth.user.telephoneNumber === null && (
                    <Alert style={{ margin: "1em 0" }} color="#0001" textColor="black" icon={faPhone}>
                        <div>
                            <p>{language("app-inform-telephone")}</p>
                            <Button style={{ margin: "0.4em 0 0 0" }} icon={faArrowRight} onClick={() => history.push("/account/edit")}>
                                {language("app-set-telephone")}
                            </Button>
                        </div>
                    </Alert>
                )}

                {!auth && (
                    <BannerBackground>
                        <BannerContainer>
                            <h2>{language("banner-title")}</h2>
                            <h3 style={{ margin: "0.8em 0" }}>{language("banner-subtitle")}</h3>
                            <p style={{ maxWidth: "80%" }}>{language("banner-text")}</p>
                            <Button
                                style={{ margin: "1.5em 0 0 0", fontSize: "1rem" }}
                                color="white"
                                icon={faArrowRight}
                                textColor="black"
                                onClick={() => history.push("/register")}>
                                {language("banner-register")}
                            </Button>
                        </BannerContainer>
                    </BannerBackground>
                )}

                {auth && (
                    <Title>
                        <RegionName>
                            {language("app-running-tests")} {region?.name}
                        </RegionName>
                        &nbsp;{region && auth?.user.admin && <OpenCloseBadge open={region?.open} />}
                    </Title>
                )}

                {region ? (
                    <>
                        {auth && auth.user.admin && (
                            <GroupContainer title="Opties" color="#5a8ee8">
                                <Toolbar>
                                    <ToolbarButton
                                        onClick={async () => {
                                            let t = await client.fetch("/test/create", {
                                                name: languageContext === "fr" ? "Nouvelle enquête" : "Nieuw onderzoek",
                                                language: languageContext,
                                                regionName: region.name,
                                            });
                                            history.push(`/admin/test/${t.test.id}/edit`);
                                        }}
                                        color="#5a8ee8"
                                        icon={faPlus}>
                                        {language("app-new-test")}
                                    </ToolbarButton>
                                    <ToolbarButton
                                        icon={faTrash}
                                        onClick={async () => {
                                            if (!window.confirm("Weet je zeker dat je deze regio wilt verwijderen?")) return;
                                            await client.fetch("/region/delete", { regionName: region.name });
                                            history.push("/select-region");
                                        }}
                                        disabled={!region || region.testsInRegion!.length > 0}>
                                        Regio verwijderen
                                    </ToolbarButton>
                                    <ToolbarButton
                                        icon={faLink}
                                        onClick={() => navigator.clipboard.writeText(`https://${window.location.host}/#${region.name}`)}>
                                        Link kopiëren
                                    </ToolbarButton>
                                    <ToolbarOpenCloseButton
                                        open={region.open}
                                        onChange={async (val) => {
                                            setRegion((await client.fetch("/region/update", { name: region.name, open: val })).region);
                                        }}
                                    />
                                </Toolbar>
                            </GroupContainer>
                        )}

                        {region.testsInRegion!.length === 0 ? (
                            <UndrawContainer>
                                <p>{language("app-no-tests")}</p>
                                <UndrawEmpty width="300px" height="300px" />
                            </UndrawContainer>
                        ) : (
                            <>{auth && <FriendInviteCard style={{ margin: "1em 0" }} registerFriendId={auth.user.id} />}</>
                        )}

                        {region.testsInRegion!.map((testRegion) => (
                            <TestCard
                                borderIcon={auth?.user.admin ? getIconForTestStatus(testRegion.test!.status) : undefined}
                                borderContent={auth?.user.admin ? capitalize(testRegion.test!.status) : undefined}
                                borderColor={auth?.user.admin ? getColorForTestStatus(testRegion.test!.status) : undefined}
                                style={{ margin: "2em 0" }}
                                key={testRegion.test!.id}
                                test={testRegion.test!}
                                onClick={() => history.push(`/apply/${testRegion.test!.id}`)}>
                                <Button
                                    style={{ marginRight: "0.3em" }}
                                    onClick={(ev) => {
                                        ev.stopPropagation();
                                        history.push(`/apply/${testRegion.test!.id}`);
                                    }}
                                    icon={faArrowRight}
                                    color="#56a44f">
                                    {language("app-apply")}
                                </Button>

                                {auth?.user.admin && (
                                    <Button
                                        color="#5a8ee8"
                                        icon={faPen}
                                        onClick={(ev) => {
                                            ev.stopPropagation();
                                            history.push(`/admin/test/${testRegion.test!.id}`);
                                        }}>
                                        Aanpassen
                                    </Button>
                                )}
                            </TestCard>
                        ))}
                    </>
                ) : (
                    <LoadingList style={{ margin: "2em 0" }} />
                )}
            </CenterContainer>
            <Footer />
        </>
    );
}
